import React from 'react';
import { Currency } from '@profitfy/pakkun-icons';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useParams } from 'react-router-dom';

import Form from '@components/common/core/Inputs/Form';
import { ECurrency } from '@domain/enums/common/ECurrency';
import { productCustomValueSchema } from '@helpers/validators/dashboard/customValues/customValues';
import Text from '@components/common/core/DataDisplay/Text';
import { currencyFormatter, currencyToNumber } from '@helpers/masks';
import { useDate } from '@helpers/hooks/useDate';
import SideModalSave from '@components/common/core/Utils/SideModalSave';
import { useToast } from '@helpers/hooks/useToast';
import customValuesCategoriesService from '@services/pages/dashboard/customValuesCategories/customValueCategories';
import { IParams } from '@domain/interfaces/IParams';
import { EGroup } from '@domain/enums/dashboard/customValuesCategories/EGroup';
import customValueService from '@services/pages/dashboard/customValue/customValue';
import { useConfig } from '@helpers/hooks/useConfig';
import { useFinancialDashboard } from '@helpers/hooks/pages/dashboard/financialDashboard/useFinancialDashboard';

import * as S from './styles';

const NewCustomAdsSideModal: React.FC = () => {
  const { utcToZonedTime, format } = useDate();
  const { toast } = useToast();
  const { storeAliasId } = useParams<IParams>();

  const {
    handleNewCustomAdsSideModalOpen,
    isNewCustomAdsSideModalOpen,
    mutateFinancialDashboard,
  } = useFinancialDashboard();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(productCustomValueSchema),
  });
  const { customValuesCategories } = customValuesCategoriesService.getCustomValuesCategories({
    storeAliasId,
  });
  const { analytics, selectedStore, user } = useConfig();

  const amountInput = register('amount');

  const [includeEndDate, setIncludeEndDate] = React.useState<boolean>(false);
  const [selectedDate, setSelectedDate] = React.useState<Date>(utcToZonedTime(new Date()));
  const [isSubmittingForm, setIsSubmittingForm] = React.useState<boolean>(false);

  const handleIncludeEndDate = React.useCallback(event => {
    if (event && !event.target) {
      setIncludeEndDate(event.checked);
    }
  }, []);

  const onSubmit = React.useCallback(
    async data => {
      setIsSubmittingForm(true);

      try {
        let foundAdsCostCategory = customValuesCategories.find(
          category =>
            category.name === 'Custo com ADS' && category.group === EGroup.MARKETING_EXPENSES,
        );

        if (!foundAdsCostCategory) {
          const {
            data: customValueCategoriesData,
          } = await customValuesCategoriesService.createCustomValueCategory({
            storeAliasId,
            data: {
              name: 'Custo com ADS',
              group: EGroup.MARKETING_EXPENSES,
              hex_color: '#9400D3',
            },
          });

          foundAdsCostCategory = customValueCategoriesData.custom_value_category;
        }

        await customValueService.createCustomValue({
          storeAliasId,
          customValuesCategoryAliasId: foundAdsCostCategory?.alias_id || '',
          data: {
            amount: currencyToNumber(data.amount),
            currency: data.currency,
            description: data.description,
            is_active: true,
            is_spend: true,
            start_date: format(utcToZonedTime(new Date()), 'yyyy-MM-dd'),
            period: data.frequency,
            end_date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : undefined,
            send_notification: false,
          },
        });

        analytics?.track(
          'Product Marketing Cost Added',
          {
            manual_fill: true,
            email: user?.email,
          },
          { context: { groupId: selectedStore?.alias_id } },
        );

        toast.success('Valor adicional criado com sucesso!');

        setIsSubmittingForm(false);

        mutateFinancialDashboard();

        handleNewCustomAdsSideModalOpen();
      } catch (error: any) {
        setIsSubmittingForm(false);
        toast.error(error?.response?.data?.message);
      }
    },
    [
      analytics,
      toast,
      storeAliasId,
      customValuesCategories,
      format,
      selectedDate,
      selectedStore,
      utcToZonedTime,
      handleNewCustomAdsSideModalOpen,
      mutateFinancialDashboard,
      user,
    ],
  );

  const onAmountChange = React.useCallback(event => {
    event.target.value = currencyFormatter(event.target.value);
  }, []);

  return (
    <S.SideModal isOpen={isNewCustomAdsSideModalOpen} toggle={handleNewCustomAdsSideModalOpen}>
      <S.Header>
        <Currency size={36} />
        <S.Title>Novo custo de ADS</S.Title>
      </S.Header>

      <S.Body>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <S.InputGroup>
            <S.Label>Moeda</S.Label>
            <S.Select {...register('currency')} autoFocus>
              <S.Option value={ECurrency.BRL}>Real</S.Option>
              <S.Option value={ECurrency.USD}>Dólar</S.Option>
            </S.Select>
            {errors.currency && <Text isErrorFeedback>{errors.currency.message}</Text>}
          </S.InputGroup>

          <S.InputGroup>
            <S.Label>Valor</S.Label>
            <S.Input {...amountInput} type="text" onChange={onAmountChange} placeholder="0,00" />
            {errors.amount && <Text isErrorFeedback>{errors.amount.message}</Text>}
          </S.InputGroup>

          <S.InputGroup>
            <S.Label>Descrição</S.Label>
            <S.Input {...register('description')} type="text" placeholder="Descrição" />
            {errors.description && <Text isErrorFeedback>{errors.description.message}</Text>}
          </S.InputGroup>

          <S.InputGroup>
            <S.Label>Frequência</S.Label>
            <S.Select {...register('frequency')}>
              <S.Option value="ONCE">Única</S.Option>
              <S.Option value="DAILY">Diária</S.Option>
              <S.Option value="WEEKLY">Semanal</S.Option>
              <S.Option value="QUARTERLY">Trimestral</S.Option>
              <S.Option value="SEMI_ANNUALY">Semestral</S.Option>
              <S.Option value="MONTHLY">Mensal</S.Option>
            </S.Select>
          </S.InputGroup>

          <S.CheckboxGroup>
            <S.Checkbox onChange={handleIncludeEndDate} id="include-date-input" />
            <S.Label htmlFor="include-date-input">Incluir data de término</S.Label>
          </S.CheckboxGroup>

          {includeEndDate && (
            <S.InputGroup>
              <S.Label>Termina em:</S.Label>
              <S.DayPicker selectedDate={selectedDate} setSelectedDate={setSelectedDate} />
            </S.InputGroup>
          )}

          <SideModalSave
            successButtonText="Salvar"
            cancelButtonText="Cancelar"
            onCancel={handleNewCustomAdsSideModalOpen}
            isLoading={isSubmittingForm}
            isDisabled={isSubmittingForm}
          />
        </Form>
      </S.Body>
    </S.SideModal>
  );
};

export default NewCustomAdsSideModal;
